import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  usePostProfileSync,
  getProfilesById,
  getGetProfilesByIdQueryKey,
} from '@/api/control/control-plane/control-plane';
import { SyncRequestSyncMode } from '@/api/control/missionImpactAPI.schemas';
import { WizardStepper } from '@/components/profiles/wizard/WizardStepper';
import { getWizardSteps, WIZARD_STEP_IDS } from '@/components/profiles/wizard/wizardSteps';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Info, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { RefreshButton } from '@/components/RefreshButton';
import { useError } from '@/contexts/ErrorContext';
import { logger } from '@/services/logger';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * Step 6: Run Initial Sync
 * Final step in the profile creation wizard
 */
export default function RunInitialSyncStep() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { handleApiError } = useError();
  const { t } = useLanguage();
  const { orgId, profileId } = useParams<{ orgId: string; profileId: string }>();

  if (!orgId || !profileId) {
    logger.error(new Error('Missing orgId or profileId in route params'), {
      context: 'RunInitialSyncStep.mount',
    });
    void navigate('/dashboard');
    return null;
  }

  const wizardSteps = getWizardSteps(WIZARD_STEP_IDS.INITIAL_SYNC);

  const [syncTriggered, setSyncTriggered] = useState(false);

  // Fetch profile to verify it exists and check if initial sync was already triggered
  const { data: profileData, isLoading: isLoadingProfile } = useQuery({
    queryKey: getGetProfilesByIdQueryKey(orgId, profileId),
    queryFn: async () => {
      const response = await getProfilesById(orgId, profileId);
      return response;
    },
    retry: 1,
  });

  const profile = profileData?.data;
  const hasTriggeredInitialSync = profile?.hasTriggeredInitialSync ?? false;

  // Redirect if profile doesn't exist or if setup is incomplete
  useEffect(() => {
    if (!isLoadingProfile && profile) {
      if (!profile.source) {
        void navigate(`/dashboard/orgs/${orgId}/profiles/${profileId}/setup/source`);
      } else if (!profile.target) {
        void navigate(`/dashboard/orgs/${orgId}/profiles/${profileId}/setup/target`);
      } else if (hasTriggeredInitialSync) {
        // If initial sync already triggered, navigate to profile detail page
        void navigate(`/dashboard/orgs/${orgId}/profiles/${profileId}`);
      }
    } else if (!isLoadingProfile && !profile) {
      void navigate(`/dashboard/orgs/${orgId}/profiles/wizard/profile`);
    }
  }, [isLoadingProfile, profile, hasTriggeredInitialSync, navigate, orgId, profileId]);

  // Trigger sync mutation
  const { mutate: triggerSync, isPending: isSyncing } = usePostProfileSync({
    mutation: {
      onSuccess: () => {
        setSyncTriggered(true);
        toast.success(t.wizard.initialSync.initialSyncTriggeredSuccessfully);
        logger.trackEvent('InitialSync.Triggered', {
          orgId,
          profileId,
        });
        // Invalidate profile query to refresh hasTriggeredInitialSync
        void queryClient.invalidateQueries({ queryKey: getGetProfilesByIdQueryKey(orgId, profileId) });
        // Navigate to profile detail page after a short delay
        setTimeout(() => {
          void navigate(`/dashboard/orgs/${orgId}/profiles/${profileId}`);
        }, 2000);
      },
      onError: (error) => {
        handleApiError(error, { context: 'RunInitialSyncStep.triggerSync', profile: profileData?.data });
      },
    },
  });

  const handleTriggerSync = () => {
    triggerSync({
      orgId,
      profileId,
      data: {
        syncMode: SyncRequestSyncMode.ResetAndFull,
      },
    });
  };

  if (isLoadingProfile) {
    return (
      <div className="space-y-6">
        <WizardStepper steps={wizardSteps} />
        <div className="flex justify-center p-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Wizard Stepper */}
      <WizardStepper steps={wizardSteps} />

      {/* Page Header */}
      <div className="mx-auto text-center">
        <h1 className="text-3xl font-bold">{t.wizard.initialSync.title}</h1>
        <p className="text-muted-foreground">{t.wizard.initialSync.description}</p>
      </div>

      {/* Form Card */}
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>{t.wizard.initialSync.cardTitle}</CardTitle>
          <CardDescription>{t.wizard.initialSync.cardDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Warning about immutable target after sync */}
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>{t.wizard.target.warning}</strong>
              </AlertDescription>
            </Alert>

            {/* Information Alert */}
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>{t.wizard.initialSync.infoAlert}</AlertDescription>
            </Alert>

            {/* Success Message */}
            {syncTriggered && (
              <Alert variant="default">
                <CheckCircle className="h-4 w-4 !text-success" />
                <AlertDescription className="text-success font-medium">
                  {t.wizard.initialSync.triggeredSuccessfully}
                </AlertDescription>
              </Alert>
            )}

            {/* Sync Status */}
            {isSyncing && (
              <Alert variant="default">
                <Loader2 className="h-4 w-4 animate-spin" />
                <AlertDescription>{t.wizard.initialSync.triggering}</AlertDescription>
              </Alert>
            )}

            {/* Actions */}
            <div className="flex justify-end pt-4 border-t">
              {syncTriggered ? (
                <Button
                  type="button"
                  disabled
                  className="min-w-[150px]"
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  {t.wizard.initialSync.triggeredButton}
                </Button>
              ) : (
                <RefreshButton
                  type="button"
                  onClick={handleTriggerSync}
                  isLoading={isSyncing}
                  className="min-w-[150px]"
                >
                  {t.wizard.initialSync.triggerInitialSync}
                </RefreshButton>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
